import { Divider } from "@material-ui/core";
import { Box, Container, Grid, Typography } from "@mui/material";
import axios from "axios";
import {
  CustomTable,
  DateTimeDisplay,
  ModalError,
  Page,
  PrimaryButton,
  SearchTextInput,
  StatusChip,
} from "components";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import Swal from "sweetalert2";
import { getErrors } from "utils";

function DownloadMonitoring() {
  const classes = globalStyles();
  const [loadingPage, setLoadingPage] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    per_page: 10,
    search: "",
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const isFetchingRef = useRef(false);

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };
  const onClickDownload = download_url =>
    Swal.fire({
      icon: "success",
      title: "Data can only be downloaded for 7 days",
      confirmButtonText: "Close",
      didRender: () => window.open(download_url),
    });

  const getDataTable = useCallback(
    async (isBackground = false) => {
      if (isBackground && isFetchingRef.current) return;
      isFetchingRef.current = true;
      if (!isBackground) {
        setLoadingPage(true);
      }
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${hardBaseUrl}/download-monitoring/table`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params: queryParams,
          }
        );
        const { data, meta } = response?.data;
        setDataTable(Array.isArray(data) ? data : []);
        const pageCount = getTotalPage(meta?.total, queryParams?.per_page || 1);
        setTableTotalPage(pageCount || 1);
      } catch (error) {
        if (!isBackground) {
          ModalError(getErrors(error?.response));
        }
      } finally {
        isFetchingRef.current = false;
        if (!isBackground) {
          setLoadingPage(false);
        }
      }
    },
    [queryParams]
  );

  const debounceDataTable = useCallback(
    debounce(() => {
      getDataTable();
    }, 500),
    [getDataTable]
  );

  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      getDataTable();
    }
  }, [queryParams, debounceDataTable, getDataTable]);

  useEffect(() => {
    const hasInProgress =
      Array.isArray(dataTable) &&
      dataTable.some(item => {
        const status = String(item?.process_status || "").toLowerCase();
        return [
          "in-progress",
          "in_progress",
          "in progress",
          "running",
          "queued",
          "pending",
          "on progress",
          "on_progress",
        ].includes(status);
      });

    if (!hasInProgress) return;

    const intervalId = setInterval(() => {
      getDataTable(true);
    }, 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, [dataTable, getDataTable]);

  return (
    <Page className={classes.root} title="Download Monitoring">
      <Container maxWidth={false}>
        <HeaderTitle
          title="Download Monitoring"
          breadcrumbData={breadcrumbData}
        />
        <Divider className={classes?.divider} />
        <Box mb="16px">
          <SearchTextInput
            placeholder="Search"
            value={queryParams?.search}
            onChange={e => handleChangeQueryParams(e?.target?.value, "search")}
          />
        </Box>
        <CustomTable
          isLoading={loadingPage}
          columnTable={columnTable({ onClickDownload })}
          data={dataTable}
          page={queryParams?.page}
          pageSize={queryParams?.per_page}
          pageCount={tableTotalPage}
          handleChangePage={(_, page) => handleChangeQueryParams(page, "page")}
          handleChangePageSize={event =>
            handleChangeQueryParams(event?.target?.value, "per_page")
          }
        />
      </Container>
    </Page>
  );
}

const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Download Monitoring",
    active: true,
  },
];
const listStatus = [
  { value: "failed", label: "Failed", type: "danger" },
  { value: "ready-to-download", label: "Ready to Download", type: "success" },
  { value: "in-progress", label: "In Progress", type: "warning" },
  { value: "archived", label: "Archived" },
];
const download_type_condition = {
  "song-list": "Song List",
  dashboard: "Dashboard",
  "ownership-conflict": "Ownership Conflict",
  "over-claim": "Over Claim",
  "song-usage": "Song Usage",
  "song-usage-report": "Song Usage",
  "dashboard-report": "Dashboard",
  report: "Report",
  "report-data": "Report",
  "double-claim": "Double Claim",
  "error-detail-upload-monitoring": "Error Detail Upload Monitoring",
  "bulk-composer-invoice-template": "Composer Invoice Template",
  "cwr-engine-export": "CWR Template",
  "matched-songs": "Potential Claim",
};
const columnTable = ({ onClickDownload }) => [
  {
    name: "download_id",
    title: "Download ID",
  },
  {
    name: "download_type",
    title: "Download Type",
    renderText: item => download_type_condition[item] || item,
  },
  {
    name: "start_date",
    title: "Start Date",
    renderText: item => <DateTimeDisplay date={item} />,
  },
  {
    name: "end_date",
    title: "End Date",
    renderText: item => <DateTimeDisplay date={item} />,
  },
  {
    name: "process_status",
    title: "Status",
    renderText: status => {
      const selectedStatus = listStatus?.find(item => item?.value === status);
      return (
        <StatusChip label={selectedStatus?.label} type={selectedStatus?.type} />
      );
    },
  },
  {
    name: "all",
    title: "Download",
    width: 180,
    renderText: item => {
      const { process_status, download_url, end_date } = item;
      const isInProgress = process_status === "in-progress";
      const isFailed = process_status === "failed";
      const endDate = new Date(end_date);
      const today = new Date();
      const differenceInDays = Math.ceil(
        (today - endDate) / (1000 * 60 * 60 * 24)
      );
      return (
        <Grid container direction="column">
          <Grid item>
            <PrimaryButton
              label={isFailed ? "Detail" : "Download"}
              disabled={(differenceInDays >= 7 || isInProgress) && !isFailed}
              width={100}
              onClick={() =>
                isFailed
                  ? ModalError("Please Inform this error message to Asaba Team")
                  : onClickDownload(download_url)
              }
            />
          </Grid>
          {!isFailed && (
            <Grid item>
              <Typography variant="caption" mt={1}>
                Only Available for 7 days
              </Typography>
            </Grid>
          )}
        </Grid>
      );
    },
  },
];

export default DownloadMonitoring;
